// bluaIQ — Financial Trust P0 (PR-F1): one safe money / percent display helper.
//
// WHY: across the app, money is rendered as `$${value.toLocaleString()}`. That
// pattern is unsafe when `value` is not a trustworthy finite number:
//   • null / undefined  → `.toLocaleString()` THROWS (white-screens the view)
//   • NaN               → renders a credible-looking "$NaN"
//   • Infinity          → renders "$∞"
//   • a numeric string   → silently wrong (string concat upstream, locale parse)
// This helper never throws and never presents an untrusted value as real money.
//
// PRINCIPLES (per Financial Trust P0 spec):
//   • A valid finite number — including 0 and large/negative values — is ALWAYS
//     displayed. We never hide a source-backed amount just because it is large.
//   • null/undefined/'' is "Unavailable" (missing), NOT a trusted $0, unless the
//     caller explicitly opts in with treatMissingAsZero.
//   • NaN / Infinity / malformed strings are rejected → "Unavailable".
//   • Suspicious-but-possibly-real values (unexpected negative, or absurdly large)
//     are STILL displayed, with `isSuspicious` set so the UI can add a subtle
//     "verify source data" hint — never a hard block, never a "bug" label.
//
// Pure + framework-free → unit-testable and reusable by Dashboard/Budget/Payments.

export type MoneyReason =
  | 'missing'
  | 'not-finite'
  | 'malformed-string'
  | 'invalid-type'
  | 'unexpected-negative'
  | 'unusually-large'

export interface MoneyDisplay {
  /** What to render. A formatted amount ($1,234) OR a bilingual "Unavailable". */
  displayValue: string
  /** True only for a real, finite, trustworthy number (0 included). */
  isValid: boolean
  /** True when the source value was absent (null/undefined/'') — distinct from $0. */
  isMissing: boolean
  /** Valid but worth a human glance (unexpected negative, or absurdly large). Still shown. */
  isSuspicious: boolean
  /** Machine-readable reason for the invalid/suspicious state. */
  reason?: MoneyReason
}

export interface MoneyOptions {
  isEs?: boolean
  /** Allow negative values without flagging them suspicious (default false). */
  allowNegative?: boolean
  /** Strictly-above this → suspicious (still displayed). Default UNUSUALLY_LARGE_USD. */
  suspiciousAbove?: number
  /** Treat null/undefined/'' as a real $0 instead of "Unavailable" (default false). */
  treatMissingAsZero?: boolean
  /** Currency symbol (default '$'). */
  currencySymbol?: string
}

// A real DFW spec-home (even a large custom) total is well under this. A value
// strictly above it almost certainly indicates a data error (cents-as-dollars,
// string concatenation, a corrupt sum) — so we add a SUBTLE "verify" hint, but we
// NEVER hide or alter a source-backed value. Deliberately very high so it cannot
// nag on a legitimately expensive project.
export const UNUSUALLY_LARGE_USD = 100_000_000

// Bilingual trust copy (EN/ES) — exported so components share one source of truth.
export const MONEY_COPY = {
  unavailable: { en: 'Unavailable', es: 'No disponible' },
  missing: { en: 'Source data missing', es: 'Faltan datos fuente' },
  verify: { en: 'Verify source data', es: 'Revisar datos fuente' },
  needsReview: { en: 'This total needs review', es: 'Este total requiere revisión' },
  overBudget: { en: 'Over budget — verify source data', es: 'Sobre presupuesto — revisar datos fuente' },
  sourceNeedsReview: { en: 'Source data needs review', es: 'Los datos fuente requieren revisión' },
} as const

export function moneyCopy(key: keyof typeof MONEY_COPY, isEs = false): string {
  return isEs ? MONEY_COPY[key].es : MONEY_COPY[key].en
}

// Coerce unknown → a finite number, or null with a reason. The single place that
// decides what counts as a trustworthy amount.
export function coerceAmount(input: unknown): { value: number | null; reason?: MoneyReason } {
  if (input === null || input === undefined || input === '') return { value: null, reason: 'missing' }
  if (typeof input === 'number') {
    return Number.isFinite(input) ? { value: input } : { value: null, reason: 'not-finite' }
  }
  if (typeof input === 'string') {
    // Accept ONLY a clean numeric string (optional $, thousands commas, one decimal
    // group, optional leading minus). Anything else is rejected — never guessed.
    const cleaned = input.trim().replace(/^\$/, '').replace(/,/g, '')
    if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return { value: null, reason: 'malformed-string' }
    const n = Number(cleaned)
    return Number.isFinite(n) ? { value: n } : { value: null, reason: 'malformed-string' }
  }
  return { value: null, reason: 'invalid-type' }
}

// Core: turn any input into a trustworthy money display + metadata.
export function formatMoney(input: unknown, opts: MoneyOptions = {}): MoneyDisplay {
  const {
    isEs = false,
    allowNegative = false,
    suspiciousAbove = UNUSUALLY_LARGE_USD,
    treatMissingAsZero = false,
    currencySymbol = '$',
  } = opts
  const { value, reason } = coerceAmount(input)

  if (value === null) {
    if (reason === 'missing' && treatMissingAsZero) {
      return { displayValue: `${currencySymbol}0`, isValid: true, isMissing: false, isSuspicious: false }
    }
    return {
      displayValue: moneyCopy('unavailable', isEs),
      isValid: false,
      isMissing: reason === 'missing',
      isSuspicious: false,
      reason,
    }
  }

  // A finite number → ALWAYS displayed (we never hide source-backed money).
  const abs = Math.abs(value)
  const displayValue = `${value < 0 ? '-' : ''}${currencySymbol}${abs.toLocaleString('en-US', { maximumFractionDigits: 2 })}`

  let isSuspicious = false
  let suspReason: MoneyReason | undefined
  if (!allowNegative && value < 0) {
    isSuspicious = true
    suspReason = 'unexpected-negative'
  } else if (value > suspiciousAbove) {
    isSuspicious = true
    suspReason = 'unusually-large'
  }

  return { displayValue, isValid: true, isMissing: false, isSuspicious, reason: suspReason }
}

// Convenience: just the string ("$1,234" or bilingual "Unavailable").
export function formatMoneyValue(input: unknown, isEs = false, opts: Omit<MoneyOptions, 'isEs'> = {}): string {
  return formatMoney(input, { ...opts, isEs }).displayValue
}

export type PercentReason = MoneyReason | 'absurd' | 'zero-denominator'

export interface PercentDisplay {
  /** Compact display: "50%", ">999%", "<-999%", or "—". Fits a narrow KPI slot. */
  displayValue: string
  isValid: boolean
  isMissing: boolean
  /** Valid number but absurd (|pct| > cap) → render capped, flag for review. */
  isSuspicious: boolean
  reason?: PercentReason
}

// Absolute percent strictly above this is treated as absurd: we do NOT render it as
// a giant raw client-facing number. A real "over budget" is plausible into a few
// hundred percent; values in the thousands+ almost always mean a tiny/near-zero
// denominator (a data error), so we cap (">999%") + flag for review instead of
// presenting a misleading "1383525624%".
export const PERCENT_DISPLAY_CAP = 999

// Safe percent from a single pre-computed value. Invalid/missing → em dash (never
// "NaN%"/"undefined%"); absurd (|pct| > cap) → capped + suspicious; else "<n>%".
export function formatPercent(input: unknown, isEs = false, opts: { cap?: number } = {}): PercentDisplay {
  void isEs // "%" + ">999%" are language-neutral; signature kept bilingual-ready
  const cap = opts.cap ?? PERCENT_DISPLAY_CAP
  const { value, reason } = coerceAmount(input)
  if (value === null) {
    return { displayValue: '—', isValid: false, isMissing: reason === 'missing', isSuspicious: false, reason }
  }
  const rounded = Math.round(value)
  if (Math.abs(rounded) > cap) {
    return {
      displayValue: rounded > 0 ? `>${cap}%` : `<-${cap}%`,
      isValid: true,
      isMissing: false,
      isSuspicious: true,
      reason: 'absurd',
    }
  }
  return { displayValue: `${rounded}%`, isValid: true, isMissing: false, isSuspicious: false }
}

// Compute a percent from numerator / denominator with full DENOMINATOR hygiene: a
// missing / zero / NaN / Infinity / malformed denominator yields NO percent (em
// dash, invalid) — we never divide by an untrustworthy denominator to fabricate a
// number. A valid-but-tiny denominator that produces an absurd percent is capped by
// formatPercent (so the real dollar values stay, the percent shows ">999%").
export function formatPercentRatio(
  numerator: unknown,
  denominator: unknown,
  opts: { isEs?: boolean; cap?: number } = {},
): PercentDisplay {
  const den = coerceAmount(denominator)
  if (den.value === null) {
    return { displayValue: '—', isValid: false, isMissing: den.reason === 'missing', isSuspicious: false, reason: den.reason }
  }
  if (den.value === 0) {
    return { displayValue: '—', isValid: false, isMissing: false, isSuspicious: false, reason: 'zero-denominator' }
  }
  const num = coerceAmount(numerator)
  if (num.value === null) {
    return { displayValue: '—', isValid: false, isMissing: num.reason === 'missing', isSuspicious: false, reason: num.reason }
  }
  return formatPercent((num.value / den.value) * 100, opts.isEs ?? false, { cap: opts.cap })
}

// Safe integer count. Invalid/missing → em dash (never "undefined").
export function formatCount(input: unknown): { displayValue: string; isValid: boolean } {
  const { value } = coerceAmount(input)
  if (value === null) return { displayValue: '—', isValid: false }
  return { displayValue: String(Math.round(value)), isValid: true }
}
